import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion'
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react'
import { StringItem } from '../App'
import { GutstringBrand } from './GutstringBrand'
import { listComments, searchStrings } from '../api/strings'
import { useFeed } from '../api/hooks'
import { stringPublicToItem } from '../utils/stringItem'
import { truncateToWords } from '../utils/truncateWords'
import { SHOWCASE_ENABLED } from '../showcase/config'
import { NewStringModal } from './NewStringModal'

const DISCOVER_PREVIEW_MAX_WORDS = 30
const SEARCH_DEBOUNCE_MS = 350
const MIN_SEARCH_LENGTH = 2
const COMMENT_ROTATE_MS = 4200
const COMMENT_MAX_STRINGS = 10
const COMMENT_REFRESH_MS = 60_000

type InlineComment = {
  text: string
  author: string
}

/** Visual variants — mobile indent + desktop drift/gap for a scattered column feel. */
const SCATTER_VARIANTS = [
  {
    mobileIndent: 'ml-4',
    width: 'max-w-[85vw] md:max-w-[92%]',
    size: 'text-xl md:text-2xl lg:text-3xl',
    desktopDrift: 'md:ml-0 lg:ml-6',
    desktopGap: 'md:mb-[5.5rem] lg:mb-[7rem]',
  },
  {
    mobileIndent: 'ml-12',
    width: 'max-w-[85vw] md:max-w-[76%]',
    size: 'text-lg md:text-xl lg:text-2xl',
    desktopDrift: 'md:ml-auto md:mr-2 lg:mr-8',
    desktopGap: 'md:mb-[3.5rem] lg:mb-[4.5rem]',
  },
  {
    mobileIndent: 'ml-6',
    width: 'max-w-[85vw] md:max-w-[96%]',
    size: 'text-2xl md:text-3xl lg:text-4xl',
    desktopDrift: 'md:ml-4 lg:ml-10',
    desktopGap: 'md:mb-[8rem] lg:mb-[10rem]',
  },
  {
    mobileIndent: 'ml-16',
    width: 'max-w-[85vw] md:max-w-[82%]',
    size: 'text-xl md:text-2xl lg:text-3xl',
    desktopDrift: 'md:ml-auto md:mr-0 lg:mr-4',
    desktopGap: 'md:mb-[4rem] lg:mb-[5.5rem]',
  },
  {
    mobileIndent: 'ml-4',
    width: 'max-w-[85vw] md:max-w-[88%]',
    size: 'text-2xl md:text-3xl lg:text-4xl',
    desktopDrift: 'md:ml-8 lg:ml-14',
    desktopGap: 'md:mb-[6.5rem] lg:mb-[8.5rem]',
  },
  {
    mobileIndent: 'ml-10',
    width: 'max-w-[85vw] md:max-w-[70%]',
    size: 'text-xl md:text-2xl',
    desktopDrift: 'md:ml-auto md:mr-6 lg:mr-12',
    desktopGap: 'md:mb-[5rem] lg:mb-[6rem]',
  },
  {
    mobileIndent: 'ml-8',
    width: 'max-w-[85vw] md:max-w-[94%]',
    size: 'text-lg md:text-xl lg:text-2xl',
    desktopDrift: 'md:ml-2 lg:ml-8',
    desktopGap: 'md:mb-[7rem] lg:mb-[9rem]',
  },
  {
    mobileIndent: 'ml-14',
    width: 'max-w-[85vw] md:max-w-[80%]',
    size: 'text-2xl md:text-3xl lg:text-4xl',
    desktopDrift: 'md:ml-auto md:mr-4 lg:mr-10',
    desktopGap: 'md:mb-[4.5rem] lg:mb-[6rem]',
  },
] as const

type ScatterVariant = (typeof SCATTER_VARIANTS)[number]
type ScatteredQuote = StringItem & ScatterVariant

function scatterFeedItems(items: StringItem[]): ScatteredQuote[] {
  return items.map((item, index) => ({
    ...item,
    ...SCATTER_VARIANTS[index % SCATTER_VARIANTS.length],
  }))
}

function splitScatterColumns(quotes: ScatteredQuote[]) {
  const left: ScatteredQuote[] = []
  const right: ScatteredQuote[] = []
  quotes.forEach((quote, index) => {
    if (index % 2 === 0) left.push(quote)
    else right.push(quote)
  })
  return { left, right }
}

interface QuoteItemProps {
  quote: ScatteredQuote
  onSelect: (s: StringItem) => void
  inlineComment?: InlineComment | null
}

const QuoteItem = ({ quote, onSelect, inlineComment }: QuoteItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.5, 0.7, 1],
    [0.2, 0.4, 1, 0.4, 0.2],
  )
  const textShadow = useTransform(
    scrollYProgress,
    [0, 0.3, 0.5, 0.7, 1],
    [
      '0 0 0px rgba(218, 165, 32, 0)',
      '0 0 10px rgba(218, 165, 32, 0.2)',
      '0 0 40px rgba(218, 165, 32, 0.8), 0 0 80px rgba(218, 165, 32, 0.4)',
      '0 0 10px rgba(218, 165, 32, 0.2)',
      '0 0 0px rgba(218, 165, 32, 0)',
    ],
  )

  const handleClick = () => onSelect(quote)
  const { preview, isTruncated } = truncateToWords(
    quote.content,
    DISCOVER_PREVIEW_MAX_WORDS,
  )

  return (
    <div
      ref={ref}
      className={`${quote.mobileIndent} md:ml-0 ${quote.desktopDrift} ${quote.desktopGap}`}
    >
      <motion.div
        className={`md:hidden ${quote.width} flex flex-col space-y-3 py-8 pr-4 cursor-pointer`}
        style={{ opacity }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        onClick={handleClick}
      >
        <motion.p
          className={`font-mono ${quote.size} text-white leading-relaxed`}
          style={{ textShadow }}
        >
          {preview}
          {isTruncated && <span className="text-white/50">…</span>}
        </motion.p>
        <span className="font-mono text-xs text-white/50 tracking-widest">
          {quote.author} · {quote.time} · {quote.date}
        </span>
        <AnimatePresence mode="wait">
          {inlineComment && (
            <motion.div
              key={`${quote.id}:${inlineComment.text}`}
              initial={{ opacity: 0, y: 8, filter: 'blur(8px)' }}
              animate={{ opacity: 0.85, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -6, filter: 'blur(8px)' }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="pt-2"
            >
              <p className="font-mono text-xs text-white/70 leading-relaxed whitespace-pre-wrap">
                {inlineComment.text}
              </p>
              <p className="pt-1 font-mono text-[10px] uppercase tracking-widest text-white/35">
                {inlineComment.author}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div
        className={`discover-scatter-quote hidden md:flex ${quote.width} flex-col space-y-4 cursor-pointer`}
        style={{ opacity }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        onClick={handleClick}
      >
        <motion.p
          className={`discover-scatter-quote-text font-mono ${quote.size} text-white leading-relaxed`}
          style={{ textShadow }}
        >
          {preview}
          {isTruncated && <span className="text-white/50">…</span>}
        </motion.p>
        <span className="font-mono text-xs text-white/50 tracking-widest">
          {quote.author} · {quote.time} · {quote.date}
        </span>
        <AnimatePresence mode="wait">
          {inlineComment && (
            <motion.div
              key={`${quote.id}:${inlineComment.text}`}
              initial={{ opacity: 0, y: 10, filter: 'blur(10px)' }}
              animate={{ opacity: 0.88, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -8, filter: 'blur(10px)' }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="pt-1"
            >
              <p className="discover-scatter-comment font-mono text-sm text-white/70 leading-relaxed whitespace-pre-wrap">
                {inlineComment.text}
              </p>
              <p className="pt-1 font-mono text-[10px] uppercase tracking-widest text-white/35">
                {inlineComment.author}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

interface DiscoverPageProps {
  onNavigate: (page: 'discover' | 'profile') => void
  onSelectString: (s: StringItem) => void
}

function SearchResultItem({
  item,
  onSelect,
}: {
  item: StringItem
  onSelect: (s: StringItem) => void
}) {
  const { preview, isTruncated } = truncateToWords(
    item.content,
    DISCOVER_PREVIEW_MAX_WORDS,
  )
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(item)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full text-left bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-6 md:p-8 hover:bg-white/10 hover:border-white/35 transition-colors"
    >
      <p className="font-mono text-lg md:text-xl text-white leading-relaxed mb-4">
        {preview}
        {isTruncated && <span className="text-white/50">…</span>}
      </p>
      <span className="font-mono text-xs text-white/50 tracking-widest">
        {item.author}
        {item.time ? ` · ${item.time}` : ''}
        {item.date ? ` · ${item.date}` : ''}
      </span>
    </motion.button>
  )
}

interface FeedQuoteItemProps {
  quote: ScatteredQuote
  onSelect: (s: StringItem) => void
  commentMap: Record<string, InlineComment[]>
  commentTick: number
}

function FeedQuoteItem({
  quote,
  onSelect,
  commentMap,
  commentTick,
}: FeedQuoteItemProps) {
  const pool = commentMap[quote.id]
  return (
    <QuoteItem
      quote={quote}
      onSelect={onSelect}
      inlineComment={
        pool?.length ? pool[commentTick % pool.length] : null
      }
    />
  )
}

export function DiscoverPage({ onNavigate, onSelectString }: DiscoverPageProps) {
  const [newStringOpen, setNewStringOpen] = useState(false)
  const [commentMap, setCommentMap] = useState<Record<string, InlineComment[]>>(
    {},
  )
  const [commentTick, setCommentTick] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<StringItem[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState('')
  const searchRequestIdRef = useRef(0)

  const { data: feedData, error: feedSWRError, isLoading: feedLoading, mutate: reloadFeed } = useFeed()
  const feedError = feedSWRError instanceof Error ? feedSWRError.message : feedSWRError ? 'Could not load strings.' : ''
  const feedItems = useMemo(() => (feedData ?? []).map(stringPublicToItem), [feedData])
  const feedQuotes = useMemo(() => scatterFeedItems(feedItems), [feedItems])
  const { left: leftQuotes, right: rightQuotes } = useMemo(
    () => splitScatterColumns(feedQuotes),
    [feedQuotes],
  )

  const feedCommentIds = useMemo(
    () => feedItems.slice(0, COMMENT_MAX_STRINGS).map((it) => it.id),
    [feedItems],
  )

  const fetchCommentMap = useCallback(async (ids: string[]) => {
    if (!ids.length) return {} as Record<string, InlineComment[]>
    const lists = await Promise.all(ids.map((id) => listComments(id).catch(() => [])))
    const nextMap: Record<string, InlineComment[]> = {}
    for (const list of lists) {
      for (const c of list) {
        const entry = { text: c.text, author: `@${c.authorUsername}` }
        nextMap[c.stringId] = nextMap[c.stringId]
          ? [...nextMap[c.stringId], entry]
          : [entry]
      }
    }
    return nextMap
  }, [])

  // Build comment pools for inline animation; refresh when feed or on an interval.
  useEffect(() => {
    let cancelled = false
    fetchCommentMap(feedCommentIds).then((nextMap) => {
      if (!cancelled) setCommentMap(nextMap)
    })
    return () => { cancelled = true }
  }, [feedCommentIds, fetchCommentMap])

  useEffect(() => {
    const refresh = () => {
      fetchCommentMap(feedCommentIds).then((nextMap) => setCommentMap(nextMap))
    }
    const interval = window.setInterval(refresh, COMMENT_REFRESH_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [feedCommentIds, fetchCommentMap])

  const trimmedSearch = searchQuery.trim()
  const isSearching = trimmedSearch.length >= MIN_SEARCH_LENGTH

  useEffect(() => {
    if (isSearching) return
    const t = window.setInterval(
      () => setCommentTick((v) => v + 1),
      COMMENT_ROTATE_MS,
    )
    return () => window.clearInterval(t)
  }, [isSearching])

  useEffect(() => {
    if (!isSearching) {
      setSearchResults([])
      setSearchError('')
      setSearchLoading(false)
      return
    }

    const requestId = ++searchRequestIdRef.current
    setSearchLoading(true)
    setSearchError('')

    const timer = window.setTimeout(async () => {
      try {
        const hits = await searchStrings(trimmedSearch)
        if (requestId !== searchRequestIdRef.current) return
        setSearchResults(hits.map(stringPublicToItem))
      } catch (err) {
        if (requestId !== searchRequestIdRef.current) return
        setSearchResults([])
        setSearchError(
          err instanceof Error ? err.message : 'Search failed. Try again.',
        )
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [trimmedSearch, isSearching])

  const clearSearch = () => {
    searchRequestIdRef.current += 1
    setSearchQuery('')
    setSearchResults([])
    setSearchError('')
    setSearchLoading(false)
  }

  return (
    <div className="min-h-screen text-white font-mono selection:bg-white/20 relative pb-32">
      <div className="fixed inset-0 z-[-1]">
        <img
          src="https://cdn.magicpatterns.com/uploads/vZFug4ywyfwyMYgcgSqvRq/GUTSTRING_COVER_BLURRED.png"
          alt="Blurred background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-3 md:px-6 py-3 md:py-4 bg-white/5 backdrop-blur-xl border-b border-white/10">
        <GutstringBrand variant="light" linkToHome className="shrink-0" />

        <div className="flex-1 min-w-0 max-w-xs md:max-w-md mx-2 md:mx-8">
          <div className="relative flex items-center">
            <SearchIcon className="absolute left-3 w-4 h-4 text-white/40 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search strings..."
              aria-label="Search strings"
              className="w-full pl-9 pr-9 py-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full font-mono text-xs text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:bg-white/15 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2.5 p-1 rounded-full text-white/45 hover:text-white/80 hover:bg-white/10"
                aria-label="Clear search"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4 shrink-0">
          <div className="hidden md:flex items-center space-x-6 font-mono text-xs tracking-widest mr-2">
            <span className="text-white border-b border-white pb-1">DISCOVER</span>
            {SHOWCASE_ENABLED && (
              <Link
                to="/showcase"
                state={{ slide: 1 }}
                className="text-white/60 hover:text-white transition-colors pb-1"
              >
                SHOWCASE
              </Link>
            )}
          </div>

          {SHOWCASE_ENABLED && (
            <Link
              to="/showcase"
              state={{ slide: 1 }}
              className="md:hidden font-mono text-[10px] tracking-widest uppercase text-white/70 hover:text-white shrink-0"
            >
              SHOWCASE
            </Link>
          )}

          <button
            type="button"
            onClick={() => setNewStringOpen(true)}
            aria-label="New string"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 hover:bg-white/15 hover:border-white/30 px-2.5 py-2 md:px-4 text-[9px] md:text-[11px] tracking-widest uppercase text-white transition-all shrink-0"
          >
            <PlusIcon className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" strokeWidth={1.5} />
            <span className="hidden sm:inline">NEW STRING</span>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {newStringOpen && (
          <NewStringModal
            onClose={() => setNewStringOpen(false)}
            onPosted={reloadFeed}
          />
        )}
      </AnimatePresence>

      <main
        className={`relative w-full pt-24 md:pt-32 pb-16 md:pb-32 px-4 md:px-6 ${
          isSearching ? 'min-h-screen' : ''
        }`}
      >
        {isSearching ? (
          <div className="max-w-2xl mx-auto space-y-4">
            {searchLoading && (
              <p className="text-center font-mono text-xs text-white/50 tracking-widest uppercase py-12">
                Searching…
              </p>
            )}
            {!searchLoading && searchError && (
              <p className="text-center font-mono text-sm text-rose-300/90 py-12">
                {searchError}
              </p>
            )}
            {!searchLoading && !searchError && searchResults.length === 0 && (
              <p className="text-center font-mono text-xs text-white/50 tracking-widest uppercase py-12">
                No strings match &ldquo;{trimmedSearch}&rdquo;
              </p>
            )}
            {!searchLoading &&
              !searchError &&
              searchResults.map((item) => (
                <SearchResultItem
                  key={item.id}
                  item={item}
                  onSelect={onSelectString}
                />
              ))}
          </div>
        ) : (
          <div className="discover-scatter-field relative w-full px-0">
            {feedLoading && (
              <p className="text-center font-mono text-xs text-white/50 tracking-widest uppercase py-24">
                Loading strings…
              </p>
            )}
            {!feedLoading && feedError && (
              <div className="text-center py-24 space-y-4">
                <p className="font-mono text-sm text-rose-300/90">{feedError}</p>
                <button
                  type="button"
                  onClick={() => reloadFeed()}
                  className="text-xs tracking-widest uppercase underline text-white/70 hover:text-white"
                >
                  Try again
                </button>
              </div>
            )}
            {!feedLoading && !feedError && feedQuotes.length === 0 && (
              <p className="text-center font-mono text-xs text-white/50 tracking-widest uppercase py-24 max-w-md mx-auto">
                No strings yet — post the first one with NEW STRING.
              </p>
            )}
            {!feedLoading &&
              !feedError &&
              feedQuotes.length > 0 && (
                <>
                  <div className="md:hidden">
                    {feedQuotes.map((quote) => (
                      <FeedQuoteItem
                        key={quote.id}
                        quote={quote}
                        onSelect={onSelectString}
                        commentMap={commentMap}
                        commentTick={commentTick}
                      />
                    ))}
                  </div>
                  <div className="hidden md:flex w-full items-start justify-between discover-scatter-columns">
                    <div className="discover-scatter-column flex-1 min-w-0">
                      {leftQuotes.map((quote) => (
                        <FeedQuoteItem
                          key={quote.id}
                          quote={quote}
                          onSelect={onSelectString}
                          commentMap={commentMap}
                          commentTick={commentTick}
                        />
                      ))}
                    </div>
                    <div className="discover-scatter-column discover-scatter-column--right flex-1 min-w-0">
                      {rightQuotes.map((quote) => (
                        <FeedQuoteItem
                          key={quote.id}
                          quote={quote}
                          onSelect={onSelectString}
                          commentMap={commentMap}
                          commentTick={commentTick}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
      </main>
    </div>
  )
}
